import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Constants } from 'constants/api';
import { describeError, logger } from 'utils/logger';
import { initLogRocket } from 'utils/logrocket';

// Safe HTML sanitization that allows basic formatting tags
const sanitizeHTML = (html: string): string => {
  // Create a temporary div element
  const div = document.createElement('div');
  
  // Basic HTML sanitization - allow only safe tags
  const cleanHTML = html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove scripts
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '') // Remove iframes
    .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '') // Remove objects
    .replace(/<embed\b[^>]*>/gi, '') // Remove embeds
    .replace(/javascript:/gi, '') // Remove javascript: URLs
    .replace(/on\w+\s*=/gi, ''); // Remove event handlers
  
  // Allow only safe tags: p, br, strong, em, a
  div.innerHTML = cleanHTML;
  
  // Remove any remaining dangerous elements
  const scripts = div.querySelectorAll('script, iframe, object, embed');
  scripts.forEach(element => element.remove());
  
  return div.innerHTML;
};

// Secure cookie consent management
const CONSENT_KEY = 'cookie-consent-choice';
const CONSENT_TIMESTAMP_KEY = 'cookie-consent-timestamp';
const MAX_CONSENT_AGE_DAYS = 365; // GDPR recommends 12 months max

export interface CookieSettings {
  agreeButtonText: string;
  cookiesContentText: string;
  cookiesPageLink: string;
  cookiesPosition: 'left' | 'right' | 'center';
  declineButtonText: string;
  showDeclineButton: string;
}

export interface CookieResponse {
  status: string;
  code: number;
  message: string;
  data: CookieSettings;
}

interface CookieConsentProps {
  onAccept?: () => void;
  onDecline?: () => void;
  clientId?: string;
}

// Check if consent is still valid (not expired)
const isConsentValid = (): boolean => {
  const timestamp = localStorage.getItem(CONSENT_TIMESTAMP_KEY);
  if (!timestamp) return false;
  
  const consentDate = new Date(timestamp);
  const now = new Date();
  const daysDiff = (now.getTime() - consentDate.getTime()) / (1000 * 60 * 60 * 24);
  
  return daysDiff <= MAX_CONSENT_AGE_DAYS;
};

// True when the visitor accepted cookies and that choice hasn't expired.
// Optional tracking (LogRocket) only starts when this is true.
export const hasAcceptedCookies = (): boolean => {
  try {
    return localStorage.getItem(CONSENT_KEY) === 'accepted' && isConsentValid();
  } catch {
    return false;
  }
};

// Secure consent storage with timestamp
const storeConsent = (choice: 'accepted' | 'declined'): void => {
  localStorage.setItem(CONSENT_KEY, choice);
  localStorage.setItem(CONSENT_TIMESTAMP_KEY, new Date().toISOString());
};

const CookieConsent: React.FC<CookieConsentProps> = ({ onAccept, onDecline, clientId }) => {
  const [cookieSettings, setCookieSettings] = useState<CookieSettings | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCookieSettings = async () => {
      try {
        // Check if user has already given valid consent
        const cookieChoice = localStorage.getItem(CONSENT_KEY);
        if (cookieChoice && isConsentValid()) {
          setIsVisible(false);
          setLoading(false);
          return;
        }

        // Clear expired consent
        if (cookieChoice && !isConsentValid()) {
          localStorage.removeItem(CONSENT_KEY);
          localStorage.removeItem(CONSENT_TIMESTAMP_KEY);
        }

        // Fetch cookie settings with retry logic
        // Not process.env: webpack 5 has no `process` in the browser, so reading it
        // threw and the banner always fell back to the default text.
        const API_ENDPOINT = Constants.API.COOKIE_SETTINGS;
        const response = await axios.get<CookieResponse>(API_ENDPOINT, {
          timeout: 5000,
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        if (response.data.status === 'success') {
          setCookieSettings(response.data.data);
          setIsVisible(true);
        } else {
          throw new Error('API returned non-success status');
        }
      } catch (error) {
        logger.warn('cookie_settings_load_failed', { error: describeError(error) });
        setError('Failed to load cookie settings');
        // Use fallback settings for GDPR compliance
        const fallbackSettings: CookieSettings = {
          agreeButtonText: "Accept",
          cookiesContentText: "<p>We use essential cookies to make our site work. We'd also like to set optional cookies to help us improve your experience and personalize content. By accepting, you agree to our use of all cookies.</p>",
          cookiesPageLink: "https://zakhealth.com/privacy-policy",
          cookiesPosition: "center",
          declineButtonText: "Reject", 
          showDeclineButton: "1"
        };
        setCookieSettings(fallbackSettings);
        setIsVisible(true);
      } finally {
        setLoading(false);
      }
    };

    fetchCookieSettings();
  }, [clientId]);

  const handleAccept = () => {
    storeConsent('accepted');
    initLogRocket();
    setIsVisible(false);
    onAccept?.();
  };

  const handleDecline = () => {
    storeConsent('declined');
    setIsVisible(false);
    onDecline?.();
  };

  const getPositionClasses = () => {
    const isMobile = window.innerWidth <= 768;
    
    switch (cookieSettings?.cookiesPosition) {
      case 'left':
      case 'right':
      case 'center':
      default:
        return isMobile ? 'bottom-4 left-4 right-4' : 'left-4 bottom-4';
    }
  };

  if (loading || !isVisible || !cookieSettings) {
    return null;
  }

  return (
    <div className={`fixed z-50 ${getPositionClasses()} bg-white rounded-xl shadow-2xl border border-gray-200 p-6 max-w-sm mx-auto`}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Cookie Policies</h3>
        <div 
          dangerouslySetInnerHTML={{ __html: sanitizeHTML(cookieSettings.cookiesContentText) }}
          className="text-sm text-gray-600 leading-relaxed"
        />
      </div>
      
      <div className="flex gap-3 mb-3">
        {cookieSettings.showDeclineButton === '1' && (
          <button
            onClick={handleDecline}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors duration-200"
          >
            {cookieSettings.declineButtonText}
          </button>
        )}
        
        <button
          onClick={handleAccept}
          className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200 shadow-sm"
        >
          {cookieSettings.agreeButtonText}
        </button>
      </div>
      
      {cookieSettings.cookiesPageLink && (
        <div className="text-center">
          <a
            href={cookieSettings.cookiesPageLink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 hover:text-blue-800 underline font-medium"
          >
            Learn more about our cookies
          </a>
        </div>
      )}
      
      {error && (
        <div className="mt-3 text-xs text-amber-600 text-center">
          Using default cookie settings
        </div>
      )}
    </div>
  );
};

export default CookieConsent;
